import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { mdiArrowLeft, mdiContentSave, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { JurisdictionSelect } from './JurisdictionSelect'
import { ConditionBuilder, stripInternalFields } from './derivations/ConditionBuilder'
import { ConditionJsonEditor } from './derivations/ConditionJsonEditor'
import { useComplianceRule, useUpdateComplianceRule, useDeleteComplianceRule, useComplianceControls } from '../hooks/useComplianceApi'
import { useAssetTypes } from '../api/queries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { useCan } from '../contexts/PermissionsContext'

function getLocalizedName(name: Record<string, string> | undefined): string {
  if (!name) return 'Unnamed'
  return name.en || name.fr || Object.values(name)[0] || 'Unnamed'
}

export function ComplianceRuleDetailPage() {
  const { org, ruleUid } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('compliance')
  // Editing a compliance rule is `compliance.write`. Without it the form stays
  // readable — every field renders read-only and the Save/Delete actions, the
  // severity picker and the condition builders are replaced by their read-only
  // rendering (spec 2026-09-04-02, "Gating").
  const canWrite = useCan('compliance.write')

  const { data: rule, isLoading, error } = useComplianceRule(org || 'demo', ruleUid || '')
  const updateMutation = useUpdateComplianceRule(org || 'demo', ruleUid || '')
  const deleteMutation = useDeleteComplianceRule(org || 'demo')
  const { data: controls = [] } = useComplianceControls(org || 'demo')
  const { data: assetTypes = [] } = useAssetTypes(org || 'demo')

  const [controlUid, setControlUid] = useState('')
  const [assetTypeUid, setAssetTypeUid] = useState('')
  const [code, setCode] = useState('')
  const [jurisdiction, setJurisdiction] = useState('*')
  const [labelEn, setLabelEn] = useState('')
  const [labelFr, setLabelFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [severity, setSeverity] = useState(3)
  const [frequencyMonths, setFrequencyMonths] = useState('')
  const [conditions, setConditions] = useState<any>({})
  const [conditionsJson, setConditionsJson] = useState('{}')
  const [conditionsJsonError, setConditionsJsonError] = useState<string | null>(null)
  const [conditionsTab, setConditionsTab] = useState('visual')

  const [check, setCheck] = useState<any>({})
  const [checkJson, setCheckJson] = useState('{}')
  const [checkJsonError, setCheckJsonError] = useState<string | null>(null)
  const [checkTab, setCheckTab] = useState('visual')

  const [autoCreateIssue, setAutoCreateIssue] = useState(true)
  const [enabled, setEnabled] = useState(true)
  const [sourceRef, setSourceRef] = useState('')
  const [evidenceDescription, setEvidenceDescription] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  useEffect(() => {
    if (rule) {
      setControlUid(rule.compliance_control_uid || '')
      setAssetTypeUid(rule.asset_type_uid || '')
      setCode(rule.code || '')
      setJurisdiction(rule.jurisdiction || '*')
      setLabelEn(rule.label?.en || '')
      setLabelFr(rule.label?.fr || '')
      setDescriptionEn(rule.description?.en || '')
      setDescriptionFr(rule.description?.fr || '')
      setSeverity(rule.severity || 3)
      setFrequencyMonths(rule.frequency_months?.toString() || '')
      const cond = rule.conditions || {}
      setConditions(cond)
      setConditionsJson(JSON.stringify(cond, null, 2))
      const chk = rule.check || {}
      setCheck(chk)
      setCheckJson(JSON.stringify(chk, null, 2))
      setAutoCreateIssue(rule.auto_create_issue)
      setEnabled(rule.enabled)
      setSourceRef(rule.source_ref || '')
      setEvidenceDescription(rule.evidence_description || '')
    }
  }, [rule])

  const ruleControl = useMemo(() => {
    if (rule && controls.length > 0) {
      return controls.find(c => c.uid === rule.compliance_control_uid)
    }
    return undefined
  }, [rule, controls])

  const breadcrumbs = useMemo(() => {
    const crumbs: { title: string; path?: string }[] = [
      { title: t('breadcrumb.home'), path: `/${org}` },
      { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
      { title: t('breadcrumb.compliance'), path: `/${org}/compliance-controls` },
    ]
    if (ruleControl) {
      crumbs.push({ title: getLocalizedName(ruleControl.name), path: `/${org}/compliance-rules?control_code=${ruleControl.code}` })
    }
    crumbs.push({ title: rule ? (getLocalizedName(rule.label) || rule.code || t('breadcrumb.edit')) : t('breadcrumb.edit') })
    return crumbs
  }, [org, rule, ruleControl, t])
  useBreadcrumbs(breadcrumbs)

  // Sync JSON strings when visual builder changes conditions/check objects
  useEffect(() => {
    setConditionsJson(JSON.stringify(stripInternalFields(conditions), null, 2))
  }, [conditions])

  useEffect(() => {
    setCheckJson(JSON.stringify(stripInternalFields(check), null, 2))
  }, [check])

  const handleConditionsJsonChange = (json: string) => {
    setConditionsJson(json)
    try {
      const parsed = JSON.parse(json)
      setConditions(parsed)
      setConditionsJsonError(null)
    } catch {
      setConditionsJsonError('Invalid JSON')
    }
  }

  const handleCheckJsonChange = (json: string) => {
    setCheckJson(json)
    try {
      const parsed = JSON.parse(json)
      setCheck(parsed)
      setCheckJsonError(null)
    } catch {
      setCheckJsonError('Invalid JSON')
    }
  }

  const handleSave = async () => {
    if (!rule) return
    if (conditionsJsonError || checkJsonError) return

    const label: Record<string, string> = {}
    if (labelEn) label.en = labelEn
    if (labelFr) label.fr = labelFr

    const description: Record<string, string> = {}
    if (descriptionEn) description.en = descriptionEn
    if (descriptionFr) description.fr = descriptionFr

    const body: any = {
      compliance_control_uid: controlUid || undefined,
      jurisdiction,
      label: Object.keys(label).length > 0 ? label : undefined,
      description: Object.keys(description).length > 0 ? description : undefined,
      severity,
      conditions: stripInternalFields(conditions),
      check: stripInternalFields(check),
      auto_create_issue: autoCreateIssue,
      enabled,
    }
    if (code.trim()) body.code = code.trim()
    body.asset_type_uid = assetTypeUid || '00000000-0000-0000-0000-000000000000'
    if (frequencyMonths) body.frequency_months = parseInt(frequencyMonths)
    if (sourceRef.trim()) body.source_ref = sourceRef.trim()
    if (evidenceDescription.trim()) body.evidence_description = evidenceDescription.trim()

    try {
      await updateMutation.mutateAsync(body)
      navigate({ to: '/$org/compliance-rules', params: { org: org || 'demo' }, search: { control_code: undefined } })
    } catch (error) {
      toast({
        title: t('common:status.error'),
        description: (error as any)?.title || t('rules.error_save'),
        variant: 'destructive'
      })
    }
  }

  const handleDelete = async () => {
    if (!rule) return
    try {
      await deleteMutation.mutateAsync(rule.uid)
      navigate({ to: '/$org/compliance-rules', params: { org: org || 'demo' }, search: { control_code: undefined } })
    } catch (error) {
      toast({
        title: t('common:status.error'),
        description: (error as any)?.title || t('rules.error_delete'),
        variant: 'destructive'
      })
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !rule) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Error</h1>
          <p className="text-muted-foreground">{(error as any)?.title || t('rules.not_found')}</p>
          <Link to="/$org/compliance-rules" params={{ org: org || 'demo' }} search={{ control_code: undefined }}>
            <Button className="mt-4"><AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />{t('rules.back_to_rules')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('rules.edit_title')}</h1>
            </div>
          </div>
          {canWrite && (
            <div className="flex items-center space-x-2">
              <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)} disabled={deleteMutation.isPending} data-testid="delete-rule-btn">
                <AppIcon path={mdiTrashCanOutline} size="sm" className="mr-2" />{t('common:actions.delete')}
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending || !!conditionsJsonError || !!checkJsonError} data-testid="save-rule-btn">
                <AppIcon path={mdiContentSave} size="sm" className="mr-2" />{t('common:actions.save')}
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('rules.form_title')}</CardTitle>
            <CardDescription>{t('rules.form_description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="control">{t('rules.field_control')}</Label>
                <select
                  id="control"
                  value={controlUid}
                  onChange={(e) => setControlUid(e.target.value)}
                  disabled={!canWrite}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs"
                  data-testid="control-select"
                >
                  <option value="">{t('rules.select_control')}</option>
                  {controls.map(c => (
                    <option key={c.uid} value={c.uid}>{getLocalizedName(c.name)} ({c.code})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset-type">{t('rules.field_asset_type')}</Label>
                <select
                  id="asset-type"
                  value={assetTypeUid}
                  onChange={(e) => setAssetTypeUid(e.target.value)}
                  disabled={!canWrite}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs"
                  data-testid="asset-type-select"
                >
                  <option value="">{t('rules.all_types')}</option>
                  {assetTypes.map(at => (
                    <option key={at.uid} value={at.uid}>{typeof at.name === 'string' ? at.name : getLocalizedName(at.name as Record<string, string>)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">{t('rules.field_code')}</Label>
                <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} readOnly={!canWrite} data-testid="code-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('rules.field_jurisdiction')}</Label>
                <JurisdictionSelect value={jurisdiction} onValueChange={setJurisdiction} disabled={!canWrite} data-testid="jurisdiction-select" />
              </div>
              <div className="space-y-2">
                <Label>{t('rules.field_severity')}</Label>
                <div className="flex gap-1">
                  {[5, 4, 3, 2, 1].map((level) => {
                    const colors = [
                      'bg-red-600 hover:bg-red-700 text-white',
                      'bg-orange-500 hover:bg-orange-600 text-white',
                      'bg-yellow-500 hover:bg-yellow-600 text-white',
                      'bg-blue-500 hover:bg-blue-600 text-white',
                      'bg-gray-400 hover:bg-gray-500 text-white',
                    ]
                    const labels = [t('rules.severity_critical'), t('rules.severity_high'), t('rules.severity_medium'), t('rules.severity_low'), t('rules.severity_lowest')]
                    const isSelected = severity === level
                    // Read-only: only the current severity is shown, as a
                    // plain badge — the other levels are write affordances.
                    if (!canWrite) {
                      if (!isSelected) return null
                      return (
                        <span
                          key={level}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium ${colors[level - 1]}`}
                          data-testid={`severity-value-${level}`}
                        >
                          {labels[level - 1]}
                        </span>
                      )
                    }
                    return (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setSeverity(level)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${isSelected ? colors[level - 1] : 'bg-muted text-muted-foreground hover:bg-muted/80'} ${isSelected ? 'ring-2 ring-offset-1 ring-offset-background ring-current' : ''}`}
                        data-testid={`severity-btn-${level}`}
                      >
                        {labels[level - 1]}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="label-en">{t('rules.field_label_en')}</Label>
              <Input id="label-en" value={labelEn} onChange={(e) => setLabelEn(e.target.value)} readOnly={!canWrite} data-testid="label-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="label-fr">{t('rules.field_label_fr')}</Label>
              <Input id="label-fr" value={labelFr} onChange={(e) => setLabelFr(e.target.value)} readOnly={!canWrite} data-testid="label-fr-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description-en">{t('rules.field_description_en')}</Label>
              <Textarea id="description-en" value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} rows={2} readOnly={!canWrite} data-testid="description-en-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-fr">{t('rules.field_description_fr')}</Label>
              <Textarea id="description-fr" value={descriptionFr} onChange={(e) => setDescriptionFr(e.target.value)} rows={2} readOnly={!canWrite} data-testid="description-fr-input" />
            </div>

            <div className="space-y-2">
              <Label>{t('rules.field_conditions')}</Label>
              <p className="text-xs text-muted-foreground">{t('rules.field_conditions_hint')}</p>
              {!canWrite ? (
                <pre
                  className="max-h-72 overflow-auto rounded-md border border-input bg-muted/40 p-3 font-mono text-xs"
                  data-testid="conditions-readonly"
                >
                  {conditionsJson}
                </pre>
              ) : (
              <Tabs value={conditionsTab} onValueChange={setConditionsTab}>
                <TabsList>
                  <TabsTrigger value="visual" data-testid="conditions-visual-tab">{t('rules.tab_visual')}</TabsTrigger>
                  <TabsTrigger value="json" data-testid="conditions-json-tab">{t('rules.tab_json')}</TabsTrigger>
                </TabsList>
                <TabsContent value="visual" className="mt-2">
                  <ConditionBuilder conditions={conditions} onChange={setConditions} org={org} defaultDateMode="absolute" />
                </TabsContent>
                <TabsContent value="json" className="mt-2">
                  <ConditionJsonEditor value={conditionsJson} onChange={handleConditionsJsonChange} error={conditionsJsonError} />
                </TabsContent>
              </Tabs>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('rules.field_check')}</Label>
              <p className="text-xs text-muted-foreground">{t('rules.field_check_hint')}</p>
              {!canWrite ? (
                <pre
                  className="max-h-72 overflow-auto rounded-md border border-input bg-muted/40 p-3 font-mono text-xs"
                  data-testid="check-readonly"
                >
                  {checkJson}
                </pre>
              ) : (
              <Tabs value={checkTab} onValueChange={setCheckTab}>
                <TabsList>
                  <TabsTrigger value="visual" data-testid="check-visual-tab">{t('rules.tab_visual')}</TabsTrigger>
                  <TabsTrigger value="json" data-testid="check-json-tab">{t('rules.tab_json')}</TabsTrigger>
                </TabsList>
                <TabsContent value="visual" className="mt-2">
                  <ConditionBuilder conditions={check} onChange={setCheck} org={org} defaultDateMode="relative" />
                </TabsContent>
                <TabsContent value="json" className="mt-2">
                  <ConditionJsonEditor value={checkJson} onChange={handleCheckJsonChange} error={checkJsonError} />
                </TabsContent>
              </Tabs>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">{t('rules.field_frequency')}</Label>
                <Input id="frequency" type="number" value={frequencyMonths} onChange={(e) => setFrequencyMonths(e.target.value)} readOnly={!canWrite} data-testid="frequency-input" />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox id="auto-create-issue" checked={autoCreateIssue} onCheckedChange={setAutoCreateIssue} disabled={!canWrite} data-testid="auto-create-issue-switch" />
                <Label htmlFor="auto-create-issue">{t('rules.field_auto_create_issue')}</Label>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox id="enabled" checked={enabled} onCheckedChange={setEnabled} disabled={!canWrite} data-testid="enabled-switch" />
                <Label htmlFor="enabled">{t('rules.field_enabled')}</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="source-ref">{t('rules.field_source_ref')}</Label>
              <Input id="source-ref" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} readOnly={!canWrite} data-testid="source-ref-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="evidence-desc">{t('rules.field_evidence_desc')}</Label>
              <Textarea id="evidence-desc" value={evidenceDescription} onChange={(e) => setEvidenceDescription(e.target.value)} rows={2} readOnly={!canWrite} data-testid="evidence-desc-input" />
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label className="text-sm text-muted-foreground">{t('rules.field_created')}</Label>
                <p className="text-sm">{rule.created_at ? new Date(rule.created_at).toLocaleString() : '-'}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('rules.field_updated')}</Label>
                <p className="text-sm">{rule.updated_at ? new Date(rule.updated_at).toLocaleString() : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('rules.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('rules.delete_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-btn">{t('common:actions.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
